import type { FindFn } from "@shared/models/analytics/base"
import { createBlankEvent } from "@shared/models/analytics/base"
import type { ClassWebsiteEditCourse } from "@shared/models/analytics/class-website"
import { getPossessiveForPlanbook } from "@shared/models/analytics/planbook"
import Patch from "@shared/models/patch-updater"
import type { PreparedAction } from "@shared/actions/action-doc-builder"
import { assign, get, partial } from "lodash-es"
import type { Narrative } from "@shared/actions/create-narrative"
import defaultClassWebsiteActivity from "./default-class-website-activity"
import defaultClassWebsiteContext from "./default-class-website-context"
import defaultClassWebsiteModels from "./default-class-website-models"

interface Payload {
  planbookId: string
  websiteId: string
  courseId: string
}

export default function addCourseToClassWebsiteAction(payload: Payload): PreparedAction {
  return {
    name: "ADD_COURSE_TO_CLASS_WEBSITE",
    payload: payload,
    patches: patches(payload),
    undoPatches: undoPatches(payload),
    narrative: partial(narrative, payload),
  }
}

function patches(payload: Payload) {
  let patch = Patch.create("class-website", payload.websiteId).addToSet(
    "relationships.courses.data",
    {
      id: payload.courseId,
      type: "course",
    }
  ).patch

  return [patch]
}

function undoPatches(payload: Payload) {
  let patch = Patch.create("class-website", payload.websiteId).pull("relationships.courses.data", {
    id: payload.courseId,
    type: "course",
  }).patch

  return [patch]
}

function narrative(payload: Payload, findFn: FindFn, userId: string): Narrative {
  let models = defaultClassWebsiteModels(payload, findFn, userId)
  let courseTitle = get(models, "course.attributes.title")
  let websiteTitle = get(models, "classWebsite.attributes.title")
  let websiteSlug = get(models, "classWebsite.attributes.slug")
  let context = assign({}, defaultClassWebsiteContext(payload, models), {
    websiteTitle,
    courseTitle,
    websiteSlug,
  })
  let newEvent: ClassWebsiteEditCourse = {
    ...createBlankEvent(),
    root_id: models.planbook.id,
    root_type: "planbook",
    date_plan_scheduled_for: null,

    primary_verb: "edit",
    primary_possessive: getPossessiveForPlanbook(findFn, userId, payload.planbookId),

    primary_object_type: "class_website",
    primary_object_id: payload.websiteId,
    primary_object_specifier: null,
    primary_object_id_and_ancestor_ids: [payload.planbookId, payload.websiteId],

    adverbial_verb: "editing",

    adverbial_object_type: "course",
    adverbial_object_id: payload.courseId,
    adverbial_object_specifier: null,
    adverbial_object_id_and_ancestor_ids: [payload.planbookId, payload.courseId],
  }

  return {
    title: "Added class to class website",
    titleForAnalytics: "Class Website - Add Course",
    description: `Added ${courseTitle} to your "${websiteTitle}" website`,
    activity: defaultClassWebsiteActivity(payload, models, "updated"),
    context,
    newAnalyticsEvents: [newEvent],
  }
}
